import React, { useState, useCallback, memo } from 'react';
import { X, Sparkles, RefreshCw, Upload, Image, Video, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { whatsappTemplatesAPI, whatsappAPI } from '../../services/api';
import StableInput from './StableInput';

const IsolatedAIGenerator = memo(({ 
  isOpen, 
  onClose, 
  onTemplateGenerated 
}) => {
  const [prompt, setPrompt] = useState('');
  const [industry, setIndustry] = useState('');
  const [language, setLanguage] = useState('en');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Media upload states
  const [headerMediaFile, setHeaderMediaFile] = useState(null);
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState('');

  const handleHeaderMediaFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeaderMediaFile(file);
      
      // Create a preview URL for immediate display
      const previewUrl = URL.createObjectURL(file);
      setHeaderMediaUrl(previewUrl);
      console.log('📱 AI Generator - Header media preview URL created:', previewUrl);
      
      // Upload the file to get the real URL
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        console.log('📤 AI Generator - Uploading header media file:', {
          name: file.name,
          size: file.size,
          type: file.type
        });
        
        const response = await whatsappAPI.uploadMedia(formData);
        
        if (response.data.success) {
          setHeaderMediaUrl(response.data.data.url);
          console.log('✅ AI Generator - Header media uploaded successfully:', {
            url: response.data.data.url,
            storageType: response.data.data.storageType,
            filename: response.data.data.filename
          });
          
          // Clean up the preview URL to free memory
          URL.revokeObjectURL(previewUrl);
        } else {
          console.error('❌ AI Generator - Header upload response not successful:', response.data);
        }
      } catch (error) {
        console.error('❌ AI Generator - Error uploading header media:', error);
      }
    }
  };

  const handleMediaFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setMediaFile(file);
      
      // Create a preview URL for immediate display
      const previewUrl = URL.createObjectURL(file);
      setMediaUrl(previewUrl);
      console.log('📱 AI Generator - Media preview URL created:', previewUrl);
      
      // Upload the file to get the real URL
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        console.log('📤 AI Generator - Uploading media file:', {
          name: file.name,
          size: file.size,
          type: file.type
        });
        
        const response = await whatsappAPI.uploadMedia(formData);
        
        if (response.data.success) {
          setMediaUrl(response.data.data.url);
          console.log('✅ AI Generator - Media uploaded successfully:', {
            url: response.data.data.url,
            storageType: response.data.data.storageType,
            filename: response.data.data.filename
          });
          
          // Clean up the preview URL to free memory
          URL.revokeObjectURL(previewUrl);
        } else {
          console.error('❌ AI Generator - Media upload response not successful:', response.data);
        }
      } catch (error) {
        console.error('❌ AI Generator - Error uploading media:', error);
      }
    }
  };

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      toast.error('Please describe your template');
      return;
    }

    setLoading(true);
    try {
      const response = await whatsappTemplatesAPI.generateAITemplate({
        prompt: prompt.trim(),
        industry,
        language,
        // Include uploaded media URLs if available
        headerMediaUrl: headerMediaUrl.startsWith('http') ? headerMediaUrl : '',
        mediaUrl: mediaUrl.startsWith('http') ? mediaUrl : ''
      });

      if (response.data.success) {
        setResult(response.data.data);
        toast.success('Template generated successfully!');
      } else {
        throw new Error(response.data.message || 'Failed to generate template');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.message || 'Failed to generate template');
    } finally {
      setLoading(false);
    }
  }, [prompt, industry, language, headerMediaUrl, mediaUrl]);

  const handleUseTemplate = useCallback(() => {
    if (result && onTemplateGenerated) {
      onTemplateGenerated(result);
      onClose();
    }
  }, [result, onTemplateGenerated, onClose]);

  const handleClose = useCallback(() => {
    setPrompt('');
    setIndustry('');
    setLanguage('en');
    setResult(null);
    setHeaderMediaFile(null);
    setHeaderMediaUrl('');
    setMediaFile(null);
    setMediaUrl('');
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">AI Template Generator</h2>
                <p className="text-purple-100">Generate templates using AI</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Describe your template
            </label>
            <StableInput
              type="textarea"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., Welcome message for new customers with discount offer"
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all placeholder-gray-500 dark:placeholder-gray-400 shadow-sm dark:shadow-md"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Industry
              </label>
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all shadow-sm dark:shadow-md"
              >
                <option value="">Select Industry</option>
                <option value="ecommerce">E-commerce</option>
                <option value="healthcare">Healthcare</option>
                <option value="education">Education</option>
                <option value="finance">Finance</option>
                <option value="technology">Technology</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Language
              </label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all shadow-sm dark:shadow-md"
              >
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="te">Telugu</option>
              </select>
            </div>
          </div>

          {/* Media Upload Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 flex items-center">
              <Upload className="w-5 h-5 mr-2" />
              Media Attachments (Optional)
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Header Media */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Header Media
                </label>
                <input
                  type="file"
                  onChange={handleHeaderMediaFileChange}
                  accept="image/*,video/*"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                />
                {headerMediaFile && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Selected: {headerMediaFile.name} ({(headerMediaFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
                {headerMediaUrl && (
                  <div className="mt-2">
                    {headerMediaUrl.includes('image') ? (
                      <Image className="w-8 h-8 text-purple-600" />
                    ) : (
                      <Video className="w-8 h-8 text-purple-600" />
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400">Preview available</p>
                  </div>
                )}
              </div>

              {/* Body Media */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Body Media
                </label>
                <input
                  type="file"
                  onChange={handleMediaFileChange}
                  accept="image/*,video/*,audio/*"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                />
                {mediaFile && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Selected: {mediaFile.name} ({(mediaFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
                {mediaUrl && (
                  <div className="mt-2">
                    {mediaUrl.includes('image') ? (
                      <Image className="w-8 h-8 text-purple-600" />
                    ) : mediaUrl.includes('video') ? (
                      <Video className="w-8 h-8 text-purple-600" />
                    ) : (
                      <FileText className="w-8 h-8 text-purple-600" />
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400">Preview available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-medium flex items-center justify-center space-x-2 disabled:opacity-50"
          >
            {loading ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            <span>Generate Template</span>
          </button>

          {result && (
            <div className="border border-purple-200 dark:border-purple-700 rounded-xl p-4 bg-purple-50 dark:bg-purple-900/20">
              <h4 className="font-semibold text-purple-800 dark:text-purple-300 mb-3">Generated Template:</h4>
              <div className="space-y-3">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Name:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{result.template_name}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Category:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">{result.category}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Content:</span>
                  <p className="mt-1 p-3 bg-white dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-300 border dark:border-gray-600">
                    {result.body_text || result.template_content}
                  </p>
                </div>
                {result.variables && result.variables.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Variables:</span>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {result.variables.map((variable, index) => (
                        <span key={index} className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-sm">
                          {variable}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={handleUseTemplate}
                className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Use This Template
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

IsolatedAIGenerator.displayName = 'IsolatedAIGenerator';

export default IsolatedAIGenerator;
